import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSystemStatus, SystemStatusData } from '../hooks/useSystemStatus';
import { useConnectionStatusOptional, ConnectionState } from '../contexts/ConnectionStatusContext';
import { useSystemHealth } from '../contexts/SystemHealthContext';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';

interface StatusTooltipProps {
  children: React.ReactNode;
}

interface TooltipPosition {
  top: number;
  right: number;
}

// Connection status labels in Portuguese
const CONNECTION_STATUS_LABELS: Record<ConnectionState, { label: string; color: string }> = {
  connected: { label: 'Conectado', color: 'text-emerald-600' },
  connecting: { label: 'Conectando...', color: 'text-yellow-600' },
  reconnecting: { label: 'Reconectando...', color: 'text-orange-600' },
  disconnected: { label: 'Desconectado', color: 'text-red-600' },
};

const StatusTooltip: React.FC<StatusTooltipProps> = ({ children }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ top: 0, right: 0 });
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error, isGeneralMode, refreshIfStale } = useSystemStatus(30000);

  // Get connection status (returns undefined if not in provider)
  const connectionStatus = useConnectionStatusOptional();

  // Get system health from context
  const { services: serviceHealth, isChecking: isCheckingHealth, checkHealth } = useSystemHealth();

  // Calculate tooltip position based on trigger element
  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      // Account for Electron title bar (40px) + navbar (56px) = 96px in Electron, or just 56px in web
      const minTop = 100; // Minimum distance from viewport top
      const calculatedTop = rect.bottom + 12; // 12px below the trigger
      setTooltipPosition({
        top: Math.max(minTop, calculatedTop),
        right: window.innerWidth - rect.right, // Align right edge with trigger
      });
    }
  }, []);

  useEffect(() => {
    if (isHovered) {
      // Calculate position and show tooltip after a short delay
      updatePosition();
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, 300);
    } else {
      // Hide tooltip immediately
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setShowTooltip(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHovered, updatePosition]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    // Refresh data if stale (older than 10 seconds)
    refreshIfStale();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Render services health section
  const renderServicesHealth = () => {
    return (
      <div className="space-y-1.5 mb-3 pb-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-medium text-gray-700">Saúde dos Serviços</h4>
          <button
            onClick={(e) => {
              e.stopPropagation();
              checkHealth();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Atualizar"
          >
            <RefreshCw className={`h-3 w-3 ${isCheckingHealth ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="space-y-1">
          {serviceHealth.map((service) => (
            <div key={service.name} className="flex items-center justify-between text-xs">
              <span className="text-gray-600 truncate">{service.displayName}</span>
              <div className="flex items-center gap-1">
                {service.status === 'healthy' ? (
                  <>
                    <CheckCircle className="h-3 w-3 text-emerald-500" />
                    <span className="text-emerald-600 font-medium">OK</span>
                  </>
                ) : service.status === 'unhealthy' ? (
                  <>
                    <XCircle className="h-3 w-3 text-red-500" />
                    <span className="text-red-600 font-medium">Erro</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-3 w-3 text-gray-400 animate-spin" />
                    <span className="text-gray-500">...</span>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render connection status section
  const renderConnectionStatus = () => {
    // In general/admin mode (no customer selected) there is no active SSE connection — hide to avoid stuck "Conectando..."
    if (!connectionStatus || isGeneralMode) return null;

    const { status } = connectionStatus;
    const config = CONNECTION_STATUS_LABELS[status.state];

    // Format last heartbeat time
    let lastHeartbeatText = '';
    if (status.lastHeartbeat) {
      const secondsAgo = Math.floor((Date.now() - status.lastHeartbeat.getTime()) / 1000);
      if (secondsAgo < 60) {
        lastHeartbeatText = `há ${secondsAgo}s`;
      } else {
        const minutesAgo = Math.floor(secondsAgo / 60);
        lastHeartbeatText = `há ${minutesAgo}min`;
      }
    }

    return (
      <div className="space-y-1.5 mb-3 pb-3 border-b border-gray-200">
        <h4 className="text-xs font-medium text-gray-700">Conexão em Tempo Real</h4>
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600">Status SSE:</span>
          <span className={`font-semibold ${config.color}`}>{config.label}</span>
        </div>
        {status.lastHeartbeat && status.state === 'connected' && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Último sinal:</span>
            <span className="font-medium text-gray-900">{lastHeartbeatText}</span>
          </div>
        )}
        {status.reconnectAttempts > 0 && status.state !== 'connected' && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-600">Tentativas:</span>
            <span className="font-medium text-orange-600">{status.reconnectAttempts}</span>
          </div>
        )}
        {status.lastError && status.state !== 'connected' && (
          <div className="text-xs text-red-600 mt-1">
            {status.lastError}
          </div>
        )}
      </div>
    );
  };

  const renderTooltipContent = () => {
    // Always show services health and connection status, even if data loading failed
    if (error || isLoading || !data) {
      // Get connection status color for header dot
      const dotColor = connectionStatus?.status.state === 'connected'
        ? 'bg-emerald-500'
        : connectionStatus?.status.state === 'disconnected'
          ? 'bg-red-500'
          : 'bg-yellow-500';
      const shouldAnimate = connectionStatus?.status.state !== 'disconnected';

      return (
        <div className="px-4 py-3 w-72">
          {/* Header */}
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
            <div className={`w-2 h-2 ${dotColor} rounded-full ${shouldAnimate ? 'animate-pulse' : ''}`} />
            <h3 className="text-sm font-semibold text-gray-900">Status do Sistema</h3>
          </div>

          {/* Services Health - always show */}
          {renderServicesHealth()}

          {/* Connection Status */}
          {renderConnectionStatus()}

          {/* Show loading or error for stats */}
          {isLoading ? (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-700 mb-1.5">Estatísticas</h4>
              <div className="h-4 bg-gray-200 rounded animate-pulse w-48" />
              <div className="h-3 bg-gray-200 rounded animate-pulse w-32" />
            </div>
          ) : error ? (
            <div className="text-xs text-gray-500">
              Estatísticas indisponíveis
            </div>
          ) : null}
        </div>
      );
    }

    return isGeneralMode ? renderGeneralModeContent(data) : renderCustomerModeContent(data);
  };

  const renderGeneralModeContent = (data: SystemStatusData) => {
    // In general mode, derive dot color from service health (not SSE state, since SSE is N/A here)
    const allHealthy = serviceHealth.every(s => s.status === 'healthy');
    const anyUnhealthy = serviceHealth.some(s => s.status === 'unhealthy');
    const dotColor = anyUnhealthy ? 'bg-red-500' : allHealthy ? 'bg-emerald-500' : 'bg-yellow-500';
    const shouldAnimate = !allHealthy;

    return (
      <div className="px-4 py-3 w-80">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
          <div className={`w-2 h-2 ${dotColor} rounded-full ${shouldAnimate ? 'animate-pulse' : ''}`} />
          <h3 className="text-sm font-semibold text-gray-900">{data.contextName}</h3>
        </div>

        {/* Services Health */}
        {renderServicesHealth()}

        {/* Connection Status */}
        {renderConnectionStatus()}

        {/* System Stats */}
        <div className="space-y-2 mb-3 pb-3 border-b border-gray-200">
          <h4 className="text-xs font-medium text-gray-700 mb-1.5">Estatísticas do Sistema</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Clientes:</span>
              <span className="font-semibold text-gray-900">{data.stats.totalCustomers || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Pessoas:</span>
              <span className="font-semibold text-gray-900">{data.stats.totalPeople}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Zonas:</span>
              <span className="font-semibold text-gray-900">{data.stats.totalZones}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Dispositivos:</span>
              <span className="font-semibold text-gray-900">{data.stats.totalDevices}</span>
            </div>
          </div>
          <div className="flex items-center justify-between text-xs pt-1">
            <span className="text-gray-600">Acessos hoje:</span>
            <span className="font-semibold text-emerald-600">{data.stats.todayEvents}</span>
          </div>
        </div>

        {/* Customers Status */}
        {data.customers && (
          <div className="space-y-1.5 mb-3 pb-3 border-b border-gray-200">
            <h4 className="text-xs font-medium text-gray-700">Status dos Clientes</h4>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                <span className="text-gray-600">{data.customers.active} ativos</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full" />
                <span className="text-gray-600">{data.customers.inactive} inativos</span>
              </div>
            </div>
          </div>
        )}

        {/* Devices Status */}
        <div className="space-y-1.5">
          <h4 className="text-xs font-medium text-gray-700">Status dos Dispositivos</h4>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" />
              <span className="text-gray-600">{data.devices.online} online</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full" />
              <span className="text-gray-600">{data.devices.offline} offline</span>
            </div>
            <div className="ml-auto">
              <span className="font-semibold text-gray-900">{data.devices.percentage}%</span>
            </div>
          </div>
          {data.devices.total > 0 && (
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${data.devices.percentage}%` }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCustomerModeContent = (data: SystemStatusData) => {
    // Get connection status color for header dot
    const dotColor = connectionStatus?.status.state === 'connected'
      ? 'bg-emerald-500'
      : connectionStatus?.status.state === 'disconnected'
        ? 'bg-red-500'
        : 'bg-yellow-500';
    const shouldAnimate = connectionStatus?.status.state !== 'disconnected';

    return (
      <div className="px-4 py-3 w-72">
        {/* Header */}
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
          <div className={`w-2 h-2 ${dotColor} rounded-full ${shouldAnimate ? 'animate-pulse' : ''}`} />
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{data.contextName}</h3>
            {data.contextType && (
              <p className="text-xs text-gray-500 capitalize">{data.contextType}</p>
            )}
          </div>
        </div>

        {/* Services Health */}
        {renderServicesHealth()}

        {/* Connection Status */}
        {renderConnectionStatus()}

        {/* Customer Stats */}
        <div className="space-y-2 mb-3 pb-3 border-b border-gray-200">
          <h4 className="text-xs font-medium text-gray-700 mb-1.5">Estatísticas</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">
                {data.contextType === 'condominio' ? 'Moradores:' :
                 data.contextType === 'empresa' || data.contextType === 'fabrica' ? 'Funcionários:' :
                 'Pessoas:'}
              </span>
              <span className="font-semibold text-gray-900">{data.stats.totalPeople}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Zonas:</span>
              <span className="font-semibold text-gray-900">{data.stats.totalZones}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Dispositivos:</span>
              <span className="font-semibold text-gray-900">{data.stats.totalDevices}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Acessos hoje:</span>
              <span className="font-semibold text-emerald-600">{data.stats.todayEvents}</span>
            </div>
          </div>
        </div>

        {/* Zones Status */}
        {data.zones && (
          <div className="space-y-1.5 mb-3 pb-3 border-b border-gray-200">
            <h4 className="text-xs font-medium text-gray-700">Status das Zonas</h4>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                <span className="text-gray-600">{data.zones.active} ativas</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full" />
                <span className="text-gray-600">{data.zones.inactive} inativas</span>
              </div>
            </div>
          </div>
        )}

        {/* Devices Status */}
        <div className="space-y-1.5">
          <h4 className="text-xs font-medium text-gray-700">Status dos Dispositivos</h4>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" />
              <span className="text-gray-600">{data.devices.online} online</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full" />
              <span className="text-gray-600">{data.devices.offline} offline</span>
            </div>
            <div className="ml-auto">
              <span className="font-semibold text-gray-900">{data.devices.percentage}%</span>
            </div>
          </div>
          {data.devices.total > 0 && (
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${data.devices.percentage}%` }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="cursor-pointer inline-block electron-no-drag"
      >
        {children}
      </div>

      {showTooltip && (
        <div
          className="fixed bg-white rounded-lg shadow-xl border border-gray-200 z-50 electron-no-drag"
          style={{
            top: `${tooltipPosition.top}px`,
            right: `${tooltipPosition.right}px`
          } as React.CSSProperties}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {/* Tooltip content */}
          {renderTooltipContent()}
        </div>
      )}
    </>
  );
};

export default StatusTooltip;
